import { Badge } from '@heidi/design-system/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { TypographyCaption, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { FileWarning, FileX2 } from 'lucide-react';
import { FormattedMessage } from 'react-intl';
import { libraryUploadRiskMessages } from './libraryUploadRisk.messages';
import type { DocumentOcrQualityFlag } from './useLibraryUploadOcrQuality';

interface DocumentOcrQualityProps {
  /**
   * The simulated text-extraction/OCR quality outcome for the document.
   * `warning` = text extracted but low confidence; `error` = text could not be
   * read reliably. `ok` documents do not render a tag.
   */
  quality: DocumentOcrQualityFlag;
  className?: string;
}

/**
 * The OCR-quality tag chip itself — a standard design-system `Badge`. Used
 * standalone on evidence citations and as the popover trigger on the library
 * document card, so both surfaces share identical styling.
 */
export const DocumentOcrQualityTag = ({
  quality,
  className,
}: DocumentOcrQualityProps) => {
  const isError = quality === 'error';

  return (
    <Badge
      color={isError ? 'red' : 'yellow'}
      size="sm"
      className={cn('gap-1', className)}
      data-testid="ocr-quality-tag"
    >
      {isError ? (
        <FileX2 size={12} className="shrink-0" />
      ) : (
        <FileWarning size={12} className="shrink-0" />
      )}
      <FormattedMessage
        {...(isError
          ? libraryUploadRiskMessages.ocrCardErrorBadge
          : libraryUploadRiskMessages.ocrCardWarningBadge)}
      />
    </Badge>
  );
};

/**
 * EVI-1566 (EV-029): tag surfaced on the library document card when the
 * (currently simulated) text-extraction/OCR quality check flags a problem.
 * Clicking the tag opens a popover explaining what the outcome means and what
 * the user can do about it.
 */
export const DocumentOcrQualityBadge = ({
  quality,
}: {
  quality: DocumentOcrQualityFlag;
}) => {
  const isError = quality === 'error';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Prevent the card-level click (preview) from firing when the tag is
          // used purely to open its explanation popover.
          onClick={(e) => e.stopPropagation()}
          className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-selected"
          data-testid="ocr-quality-badge"
        >
          <DocumentOcrQualityTag quality={quality} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-72 p-3"
        align="start"
        data-testid="ocr-quality-popover"
      >
        <div className="space-y-1">
          <TypographyP2Bold>
            <FormattedMessage
              {...(isError
                ? libraryUploadRiskMessages.ocrPopoverErrorTitle
                : libraryUploadRiskMessages.ocrPopoverWarningTitle)}
            />
          </TypographyP2Bold>
          <TypographyCaption className="block text-text-secondary">
            <FormattedMessage
              {...(isError
                ? libraryUploadRiskMessages.ocrPopoverErrorBody
                : libraryUploadRiskMessages.ocrPopoverWarningBody)}
            />
          </TypographyCaption>
        </div>
      </PopoverContent>
    </Popover>
  );
};
